import { useQuery } from '@tanstack/react-query';
import {
  fetchPosts,
  fetchPost,
  fetchProjects,
  fetchProject,
  fetchFAQs,
  fetchTestimonials,
  fetchTeamMembers,
  fetchEvents,
  fetchHeroSettings,
  fetchContactSettings,
  fetchNavigationSettings,
  fetchFooterSettings,
  fetchPartnerLogos,
  fetchHomepageFields,
  fetchCompanyPageFields,
  fetchSolutionsPageFields,
  fetchWorkPageFields,
  fetchMegaNavSettings,
  fetchPatentDisclaimerSettings,
  fetchVixiMainPageFields,
  fetchVixiCaptureFields,
  fetchVixiMagicFields,
  fetchVixiStreamingFields,
  fetchVixiLightshowFields,
  fetchVixiAddonsFields,
  fetchVixiLicensingFields,
  fetchPageByUri,
  isWordPressConfigured,
} from '@/lib/wordpress';
import type {
  BlogPost,
  FAQ,
  Testimonial,
  TeamMember,
  Project,
  FeaturedEvent,
  HeroSettings,
  ContactSettings,
  NavigationSettings,
  FooterSettings,
  PartnerLogo,
  PartnersSectionSettings,
  HomepageFields,
  CompanyPageFields,
  SolutionsPageFields,
  WorkPageFields,
  MegaNavSettings,
  PatentDisclaimerSettings,
  VixiMainPageFields,
  VixiCaptureFields,
  VixiMagicFields,
  VixiStreamingFields,
  VixiLightshowFields,
  VixiAddonsFields,
  VixiLicensingFields,
  WPPageContent,
} from '@/types/wordpress';

// Query key factory for consistent cache keys
export const wpQueryKeys = {
  posts: (params?: { page?: number; perPage?: number }) => ['wp', 'posts', params] as const,
  post: (slug: string) => ['wp', 'post', slug] as const,
  projects: () => ['wp', 'projects'] as const,
  project: (slug: string) => ['wp', 'project', slug] as const,
  faqs: () => ['wp', 'faqs'] as const,
  testimonials: () => ['wp', 'testimonials'] as const,
  team: () => ['wp', 'team'] as const,
  events: () => ['wp', 'events'] as const,
  heroSettings: () => ['wp', 'heroSettings'] as const,
  contactSettings: () => ['wp', 'contactSettings'] as const,
  navigationSettings: () => ['wp', 'navigationSettings'] as const,
  footerSettings: () => ['wp', 'footerSettings'] as const,
  partnerLogos: () => ['wp', 'partnerLogos'] as const,
  homepageFields: () => ['wp', 'homepageFields'] as const,
  companyFields: () => ['wp', 'companyFields'] as const,
  solutionsFields: () => ['wp', 'solutionsFields'] as const,
  workFields: () => ['wp', 'workFields'] as const,
  megaNavSettings: () => ['wp', 'megaNavSettings'] as const,
  patentDisclaimer: () => ['wp', 'patentDisclaimer'] as const,
  vixiMainFields: () => ['wp', 'vixiMainFields'] as const,
  vixiCaptureFields: () => ['wp', 'vixiCaptureFields'] as const,
  vixiMagicFields: () => ['wp', 'vixiMagicFields'] as const,
  vixiStreamingFields: () => ['wp', 'vixiStreamingFields'] as const,
  vixiLightshowFields: () => ['wp', 'vixiLightshowFields'] as const,
  vixiAddonsFields: () => ['wp', 'vixiAddonsFields'] as const,
  vixiLicensingFields: () => ['wp', 'vixiLicensingFields'] as const,
  pageByUri: (uri: string) => ['wp', 'pageByUri', uri] as const,
};

// Default query options with 5 minute stale time
const defaultOptions = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 30 * 60 * 1000, // 30 minutes
  retry: 2,
  refetchOnWindowFocus: false,
};

// Faster options for page-level CMS fields that editors update frequently
const contentOptions = {
  staleTime: 30 * 1000, // 30 seconds — editors see changes quickly
  gcTime: 30 * 60 * 1000, // 30 minutes
  retry: 2,
  refetchOnWindowFocus: true, // Refetch when editor tabs back to preview
};

// ============================================
// BLOG POSTS
// ============================================

export function useWordPressPosts(params?: { page?: number; perPage?: number }) {
  return useQuery<BlogPost[], Error>({
    queryKey: wpQueryKeys.posts(params),
    queryFn: () => fetchPosts(params),
    enabled: isWordPressConfigured(),
    ...defaultOptions,
  });
}

export function useWordPressPost(slug: string) {
  return useQuery<BlogPost, Error>({
    queryKey: wpQueryKeys.post(slug),
    queryFn: () => fetchPost(slug),
    enabled: isWordPressConfigured() && Boolean(slug),
    ...defaultOptions,
  });
}

// ============================================
// PROJECTS
// ============================================

export function useWordPressProjects() {
  return useQuery<Project[], Error>({
    queryKey: wpQueryKeys.projects(),
    queryFn: fetchProjects,
    enabled: isWordPressConfigured(),
    ...defaultOptions,
  });
}

export function useWordPressProject(slug: string) {
  return useQuery<Project, Error>({
    queryKey: wpQueryKeys.project(slug),
    queryFn: () => fetchProject(slug),
    enabled: isWordPressConfigured() && Boolean(slug),
    ...defaultOptions,
  });
}

// ============================================
// FAQs
// ============================================

export function useWordPressFAQs() {
  return useQuery<FAQ[], Error>({
    queryKey: wpQueryKeys.faqs(),
    queryFn: async () => {
      const faqs = await fetchFAQs();
      return faqs.sort((a, b) => a.order - b.order);
    },
    enabled: isWordPressConfigured(),
    ...defaultOptions,
  });
}

// ============================================
// TESTIMONIALS
// ============================================

export function useWordPressTestimonials() {
  return useQuery<Testimonial[], Error>({
    queryKey: wpQueryKeys.testimonials(),
    queryFn: fetchTestimonials,
    enabled: isWordPressConfigured(),
    ...defaultOptions,
  });
}

// ============================================
// TEAM MEMBERS
// ============================================

export function useWordPressTeam() {
  return useQuery<TeamMember[], Error>({
    queryKey: wpQueryKeys.team(),
    queryFn: fetchTeamMembers,
    enabled: isWordPressConfigured(),
    ...defaultOptions,
  });
}

// ============================================
// FEATURED EVENTS
// ============================================

export function useWordPressEvents() {
  return useQuery<FeaturedEvent[], Error>({
    queryKey: wpQueryKeys.events(),
    queryFn: async () => {
      const events = await fetchEvents();
      return events.sort((a, b) => a.order - b.order);
    },
    enabled: isWordPressConfigured(),
    ...defaultOptions,
  });
}

// ============================================
// UTILITY HOOKS
// ============================================

export function useWordPressStatus() {
  return {
    isConfigured: isWordPressConfigured(),
    url: import.meta.env.VITE_WORDPRESS_URL || null,
  };
}

export function useWordPressPageByUri(uri: string) {
  return useQuery<WPPageContent | null, Error>({
    queryKey: wpQueryKeys.pageByUri(uri),
    queryFn: () => fetchPageByUri(uri),
    enabled: isWordPressConfigured() && Boolean(uri),
    ...contentOptions,
  });
}

// ============================================
// HERO SETTINGS
// ============================================

export function useWordPressHeroSettings() {
  return useQuery<HeroSettings | null, Error>({
    queryKey: wpQueryKeys.heroSettings(),
    queryFn: fetchHeroSettings,
    enabled: isWordPressConfigured(),
    ...defaultOptions,
  });
}

// ============================================
// CONTACT SETTINGS
// ============================================

export function useWordPressContactSettings() {
  return useQuery<ContactSettings | null, Error>({
    queryKey: wpQueryKeys.contactSettings(),
    queryFn: fetchContactSettings,
    enabled: isWordPressConfigured(),
    ...defaultOptions,
  });
}

// ============================================
// NAVIGATION SETTINGS
// ============================================

export function useWordPressNavigation() {
  return useQuery<NavigationSettings | null, Error>({
    queryKey: wpQueryKeys.navigationSettings(),
    queryFn: fetchNavigationSettings,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

// ============================================
// FOOTER SETTINGS
// ============================================

export function useWordPressFooterSettings() {
  return useQuery<FooterSettings | null, Error>({
    queryKey: wpQueryKeys.footerSettings(),
    queryFn: fetchFooterSettings,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

// ============================================
// PARTNER LOGOS
// ============================================

export function useWordPressPartnerLogos() {
  return useQuery<{ logos: PartnerLogo[]; settings: PartnersSectionSettings | null }, Error>({
    queryKey: wpQueryKeys.partnerLogos(),
    queryFn: fetchPartnerLogos,
    enabled: isWordPressConfigured(),
    ...defaultOptions,
  });
}

// ============================================
// PAGE-LEVEL FIELD HOOKS
// ============================================

export function useWordPressHomepageFields() {
  return useQuery<HomepageFields | null, Error>({
    queryKey: wpQueryKeys.homepageFields(),
    queryFn: fetchHomepageFields,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

export function useWordPressCompanyFields() {
  return useQuery<CompanyPageFields | null, Error>({
    queryKey: wpQueryKeys.companyFields(),
    queryFn: fetchCompanyPageFields,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

export function useWordPressSolutionsFields() {
  return useQuery<SolutionsPageFields | null, Error>({
    queryKey: wpQueryKeys.solutionsFields(),
    queryFn: fetchSolutionsPageFields,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

export function useWordPressWorkFields() {
  return useQuery<WorkPageFields | null, Error>({
    queryKey: wpQueryKeys.workFields(),
    queryFn: fetchWorkPageFields,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

// ============================================
// MEGA NAVIGATION SETTINGS
// ============================================

export function useWordPressMegaNavSettings() {
  return useQuery<MegaNavSettings | null, Error>({
    queryKey: wpQueryKeys.megaNavSettings(),
    queryFn: fetchMegaNavSettings,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

// ============================================
// PATENT DISCLAIMER SETTINGS
// ============================================

export function useWordPressPatentDisclaimer() {
  return useQuery<PatentDisclaimerSettings | null, Error>({
    queryKey: wpQueryKeys.patentDisclaimer(),
    queryFn: fetchPatentDisclaimerSettings,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

// ============================================
// VIXI MAIN PAGE FIELDS
// ============================================

export function useWordPressVixiMainFields() {
  return useQuery<VixiMainPageFields | null, Error>({
    queryKey: wpQueryKeys.vixiMainFields(),
    queryFn: fetchVixiMainPageFields,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

// ============================================
// VIXI SUB-PAGE HOOKS
// ============================================

export function useWordPressVixiCapture() {
  return useQuery<VixiCaptureFields | null, Error>({
    queryKey: wpQueryKeys.vixiCaptureFields(),
    queryFn: fetchVixiCaptureFields,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

export function useWordPressVixiMagic() {
  return useQuery<VixiMagicFields | null, Error>({
    queryKey: wpQueryKeys.vixiMagicFields(),
    queryFn: fetchVixiMagicFields,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

export function useWordPressVixiStreaming() {
  return useQuery<VixiStreamingFields | null, Error>({
    queryKey: wpQueryKeys.vixiStreamingFields(),
    queryFn: fetchVixiStreamingFields,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

export function useWordPressVixiLightshow() {
  return useQuery<VixiLightshowFields | null, Error>({
    queryKey: wpQueryKeys.vixiLightshowFields(),
    queryFn: fetchVixiLightshowFields,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

export function useWordPressVixiAddons() {
  return useQuery<VixiAddonsFields | null, Error>({
    queryKey: wpQueryKeys.vixiAddonsFields(),
    queryFn: fetchVixiAddonsFields,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}

export function useWordPressVixiLicensing() {
  return useQuery<VixiLicensingFields | null, Error>({
    queryKey: wpQueryKeys.vixiLicensingFields(),
    queryFn: fetchVixiLicensingFields,
    enabled: isWordPressConfigured(),
    ...contentOptions,
  });
}
